import * as THREE from "three";
import { periodFromPercent, phaseAt } from "./clock";
import { Emitter } from "./emitters";
import { isCollider, PlacedObject } from "./objects";
import { AppContext } from "./appContext";

interface TransformGraphDeps {
  objectById(id: string): PlacedObject | null;
  emitterById(id: string): Emitter | null;
  nodeGraphActive(): boolean;
  flatten2d(): boolean;
}

const DEG2RAD = Math.PI / 180;
const TWO_PI = Math.PI * 2;

/**
 * Transform-chain resolution (base→live pose). Owns the per-frame transform pass
 * that walks each transform-in entity's orbit-parent chain, stages live poses,
 * snaps disconnected ones back to base, finite-differences collider surface
 * velocity, repacks the SDF, and re-uploads moving emitters. The render loop must
 * call the returned `applyTransforms(dt)` in its existing slot (before
 * particles.update) to keep behavior unchanged.
 */
export function initTransformGraph(ctx: AppContext, deps: TransformGraphDeps): { applyTransforms(dt: number): void } {
  const { particles, graph, field, emitterField, evaluator } = ctx;
  const { objectById, emitterById, nodeGraphActive, flatten2d } = deps;

  const prevCollisionPos = new Map<string, THREE.Vector3>();
  const drivenObjectIds = new Set<string>();
  const drivenEmitterIds = new Set<string>();
  let emitterMotionActive = false;
  const orbitEuler = new THREE.Euler();
  const orbitR = new THREE.Quaternion();
  const orbitOffset = new THREE.Vector3();
  const orbitVel = new THREE.Vector3();
  const rigidInvRot = new THREE.Quaternion();
  const rigidOffset = new THREE.Vector3();

  function axisVibration(amp: number, period: number, phasePct: number, elapsed: number, basePeriod: number, clockSync: boolean): number {
    const periodSec = clockSync ? periodFromPercent(basePeriod, period) : period;
    if (amp === 0 || periodSec <= 0) {
      return 0;
    }
    return amp * Math.sin(TWO_PI * phaseAt(elapsed, periodSec, phasePct));
  }

  function orbitAngle(rotSpeed: number, elapsed: number, basePeriod: number, clockSync: boolean): number {
    return clockSync ? TWO_PI * rotSpeed * (elapsed / basePeriod) : rotSpeed * DEG2RAD * elapsed;
  }

  /**
   * Live pose of a child parented to an orbit, in the Unity nested model: the
   * orbit's parent rotation R(t) spins the child's base offset from the orbit's
   * (base) pivot and child translation T(t) adds a per-local-axis sine, then the
   * orbit's own live pivot positions the result — so a nested orbit's children
   * ride its motion. Both layers read the orbit's clockSync flag. Writes into
   * outPos/outRot; never mutates any base pose. While flatten2d() is true the
   * out-of-plane terms (rot x/y, z vibration) read as 0 — the stored params are
   * untouched, so returning to the 3D view restores the full motion.
   *
   * When the orbit is target-wired (evaluator holds its progress) the same
   * composition runs in target-pose mode: rot XYZ are read as absolute degrees
   * scaled by progress (Euler-interpolated, NOT quaternion-slerped — slerp would
   * lose a ±360° turn since that quaternion equals identity) and amp XYZ as a
   * signed local offset scaled by progress, so progress 0 is the base pose and 1
   * the full target pose. Time-driven terms (elapsed, sine, clockSync) are unused.
   */
  function computeNestedPose(orbit: PlacedObject, pivotLive: THREE.Vector3, childBasePos: THREE.Vector3, childBaseRot: THREE.Quaternion, elapsed: number, basePeriod: number, outPos: THREE.Vector3, outRot: THREE.Quaternion): void {
    const op = orbit.orbit;
    const flat = flatten2d();
    const progress = evaluator.orbitProgressOf(orbit.id);
    if (progress !== undefined) {
      orbitEuler.set(
        flat ? 0 : op.rotSpeed.x * DEG2RAD * progress,
        flat ? 0 : op.rotSpeed.y * DEG2RAD * progress,
        op.rotSpeed.z * DEG2RAD * progress,
        "XYZ",
      );
      orbitR.setFromEuler(orbitEuler);
      orbitOffset.copy(childBasePos).sub(orbit.position);
      orbitOffset.x += op.amp.x * progress;
      orbitOffset.y += op.amp.y * progress;
      if (!flat) {
        orbitOffset.z += op.amp.z * progress;
      }
      orbitOffset.applyQuaternion(orbitR);
      outPos.copy(pivotLive).add(orbitOffset);
      outRot.copy(orbitR).multiply(childBaseRot);
      return;
    }
    const cs = op.clockSync;
    orbitEuler.set(
      flat ? 0 : orbitAngle(op.rotSpeed.x, elapsed, basePeriod, cs),
      flat ? 0 : orbitAngle(op.rotSpeed.y, elapsed, basePeriod, cs),
      orbitAngle(op.rotSpeed.z, elapsed, basePeriod, cs),
      "XYZ",
    );
    orbitR.setFromEuler(orbitEuler);
    orbitOffset.copy(childBasePos).sub(orbit.position);
    orbitOffset.x += axisVibration(op.amp.x, op.periodPct.x, op.phasePct.x, elapsed, basePeriod, cs);
    orbitOffset.y += axisVibration(op.amp.y, op.periodPct.y, op.phasePct.y, elapsed, basePeriod, cs);
    if (!flat) {
      orbitOffset.z += axisVibration(op.amp.z, op.periodPct.z, op.phasePct.z, elapsed, basePeriod, cs);
    }
    orbitOffset.applyQuaternion(orbitR);
    outPos.copy(pivotLive).add(orbitOffset);
    outRot.copy(orbitR).multiply(childBaseRot);
  }

  /**
   * Live pose of a child rigidly parented to a group: the group is a transform parent
   * with no motion of its own, so the child rides the delta between the group's base
   * pose and its current live pose (the latter non-trivial only when the group is
   * itself driven by an orbit / group above). At root the delta is identity, so the
   * child sits at its base — group drag-follow is delivered by co-moving member bases,
   * not here. Writes into outPos/outRot; never mutates any base pose.
   */
  function rigidChildPose(group: PlacedObject, groupLive: { pos: THREE.Vector3; quat: THREE.Quaternion }, childBasePos: THREE.Vector3, childBaseRot: THREE.Quaternion, outPos: THREE.Vector3, outRot: THREE.Quaternion): void {
    rigidInvRot.copy(group.rotation).invert();
    rigidOffset.copy(childBasePos).sub(group.position).applyQuaternion(rigidInvRot).applyQuaternion(groupLive.quat);
    outPos.copy(groupLive.pos).add(rigidOffset);
    outRot.copy(groupLive.quat).multiply(rigidInvRot).multiply(childBaseRot);
  }

  /**
   * Per-frame transform pass. Resolves each transform-in entity's live pose by
   * walking up its orbit-parent chain (parents first, so nested orbits and
   * orbit-driven emitters ride their parent), stages those poses onto the object
   * / emitter fields, snaps disconnected ones back to base, derives collider
   * surface velocity by finite-differencing collision position (orbit + drag),
   * repacks the SDF, and re-uploads emitter poses while any emitter moves. Full
   * repack is negligible at MAX_OBJECTS = 32. While nodeGraphActive() is false
   * the edge walk sees an empty graph, so every driven entity snaps back to its
   * base pose on the next frame (wires behave as disconnected).
   */
  function applyTransforms(dt: number): void {
    const clockTime = particles.clockTimeSec();
    const basePeriod = particles.clockPeriodSeconds();

    const childToParent = new Map<string, string>();
    for (const e of nodeGraphActive() ? graph.all() : []) {
      if (e.fromPort === "transformOut" && e.toPort === "transformIn") {
        const parent = objectById(e.from);
        if (parent && (parent.type === "orbit" || parent.type === "group")) {
          childToParent.set(e.to, e.from);
        }
      }
    }

    const memo = new Map<string, { pos: THREE.Vector3; quat: THREE.Quaternion } | null>();
    const resolving = new Set<string>();
    const liveOf = (id: string): { pos: THREE.Vector3; quat: THREE.Quaternion } | null => {
      if (memo.has(id)) {
        return memo.get(id) as { pos: THREE.Vector3; quat: THREE.Quaternion } | null;
      }
      const obj = objectById(id);
      const em = obj ? null : emitterById(id);
      const basePos = obj ? obj.position : em ? em.position : null;
      const baseRot = obj ? obj.rotation : em ? em.rotation : null;
      if (!basePos || !baseRot) {
        return null;
      }
      const result = { pos: new THREE.Vector3(), quat: new THREE.Quaternion() };
      const parentId = childToParent.get(id);
      const parent = parentId ? objectById(parentId) : null;
      if (!parent || (parent.type !== "orbit" && parent.type !== "group") || resolving.has(id)) {
        result.pos.copy(basePos);
        result.quat.copy(baseRot);
      } else if (parent.type === "orbit" && !parent.orbit.play && evaluator.orbitProgressOf(parent.id) === undefined) {
        memo.set(id, null);
        return null;
      } else {
        resolving.add(id);
        const parentLive = liveOf(parentId as string);
        resolving.delete(id);
        if (!parentLive) {
          memo.set(id, null);
          return null;
        } else if (parent.type === "orbit") {
          computeNestedPose(parent, parentLive.pos, basePos, baseRot, clockTime, basePeriod, result.pos, result.quat);
        } else {
          rigidChildPose(parent, parentLive, basePos, baseRot, result.pos, result.quat);
        }
      }
      memo.set(id, result);
      return result;
    };

    const nextObj = new Set<string>();
    const nextEm = new Set<string>();
    for (const id of childToParent.keys()) {
      const live = liveOf(id);
      if (!live) {
        continue;
      }
      if (objectById(id)) {
        field.setLivePose(id, live.pos, live.quat);
        nextObj.add(id);
      } else if (emitterById(id)) {
        emitterField.setLivePose(id, live.pos, live.quat);
        nextEm.add(id);
      }
    }
    for (const id of drivenObjectIds) {
      if (!nextObj.has(id)) {
        field.clearLivePose(id);
      }
    }
    for (const id of drivenEmitterIds) {
      if (!nextEm.has(id)) {
        emitterField.clearLivePose(id);
      }
    }
    drivenObjectIds.clear();
    for (const id of nextObj) {
      drivenObjectIds.add(id);
    }
    drivenEmitterIds.clear();
    for (const id of nextEm) {
      drivenEmitterIds.add(id);
    }

    const invDt = dt > 1e-4 ? 1 / dt : 0;
    const seen = new Set<string>();
    for (const o of field.list()) {
      if (!isCollider(o.type)) {
        continue;
      }
      seen.add(o.id);
      const cur = field.liveOrBasePosition(o.id);
      if (!cur) {
        continue;
      }
      const prev = prevCollisionPos.get(o.id);
      if (prev && invDt > 0) {
        orbitVel.copy(cur).sub(prev).multiplyScalar(invDt);
      } else {
        orbitVel.set(0, 0, 0);
      }
      field.setVelocity(o.id, orbitVel);
      let store = prevCollisionPos.get(o.id);
      if (!store) {
        store = new THREE.Vector3();
        prevCollisionPos.set(o.id, store);
      }
      store.copy(cur);
    }
    for (const id of [...prevCollisionPos.keys()]) {
      if (!seen.has(id)) {
        prevCollisionPos.delete(id);
      }
    }
    field.repack();

    const emitterMoving = nextEm.size > 0;
    if (emitterMoving || emitterMotionActive) {
      particles.setEmitters(emitterField.liveList());
    }
    emitterMotionActive = emitterMoving;
  }

  return { applyTransforms };
}
